import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AnalistaDataService } from '../../../services/v1/AnalistaController/analista-data.service';

export interface CompromissoCustomizado {
  id?: string;
  titulo?: string;
  descricao?: string;
  data_compromisso?: string;
  data_criacao?: string;
  metadata?: string;
  employee_id?: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  data: CompromissoCustomizado[];
}

@Injectable({
  providedIn: 'root'
})
export class CompromissosCustomizadosCacheService {
  private readonly CACHE_KEY = 'compromissos-customizados-cache';
  private readonly CACHE_TIMESTAMP_KEY = 'compromissos-customizados-cache-timestamp';
  private readonly CACHE_DURATION = 15 * 60 * 1000; // 15 minutos em millisegundos

  private compromissosSubject = new BehaviorSubject<CompromissoCustomizado[]>([]);
  public compromissos$ = this.compromissosSubject.asObservable();

  constructor(private analistaService: AnalistaDataService) {}

  carregarCompromissos(forcarRecarregamento: boolean = false): Observable<CompromissoCustomizado[]> {
    if (!forcarRecarregamento && this.isCacheValido()) {
      const compromissosCache = this.obterCompromissosDoCache();
      if (compromissosCache.length > 0) {
        this.compromissosSubject.next(compromissosCache);
        return of(compromissosCache);
      }
    }

    // Buscar compromissos dos últimos 3 meses e próximos 6 meses
    const dataInicio = new Date();
    dataInicio.setMonth(dataInicio.getMonth() - 3);
    
    const dataFim = new Date();
    dataFim.setMonth(dataFim.getMonth() + 6);

    return this.analistaService.GetCompromissos(undefined, dataInicio, dataFim)
      .pipe(
        map((response: any) => {
          if (response.success && Array.isArray(response.data)) {
            // Filtrar apenas compromissos customizados
            return response.data;
          }
          return [];
        }),
        tap(compromissos => {
          this.salvarCompromissosNoCache(compromissos);
          this.compromissosSubject.next(compromissos);
        })
      );
  }

  obterCompromissosHoje(): CompromissoCustomizado[] {
    const compromissos = this.compromissosSubject.value;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    return compromissos.filter(compromisso => {
      if (!compromisso.data_compromisso) return false;
      const dataCompromisso = new Date(compromisso.data_compromisso);
      dataCompromisso.setHours(0, 0, 0, 0);
      return dataCompromisso.getTime() === hoje.getTime();
    });
  }

  obterCompromissosDoMes(ano: number, mes: number): CompromissoCustomizado[] {
    const compromissos = this.compromissosSubject.value;
    
    return compromissos.filter(compromisso => {
      if (!compromisso.data_compromisso) return false;
      const dataCompromisso = new Date(compromisso.data_compromisso);
      return dataCompromisso.getFullYear() === ano && dataCompromisso.getMonth() === mes;
    });
  }

  obterCompromissosDoDia(data: Date): CompromissoCustomizado[] {
    const compromissos = this.compromissosSubject.value;
    const dataComparacao = new Date(data);
    dataComparacao.setHours(0, 0, 0, 0);

    return compromissos.filter(compromisso => {
      if (!compromisso.data_compromisso) return false;
      const dataCompromisso = new Date(compromisso.data_compromisso);
      dataCompromisso.setHours(0, 0, 0, 0);
      return dataCompromisso.getTime() === dataComparacao.getTime();
    });
  }

  adicionarCompromisso(compromisso: CompromissoCustomizado): void {
    const compromissosAtuais = this.compromissosSubject.value;
    const novosCompromissos = [...compromissosAtuais, compromisso];
    this.compromissosSubject.next(novosCompromissos);
    this.salvarCompromissosNoCache(novosCompromissos);
  }

  atualizarCompromisso(compromissoAtualizado: CompromissoCustomizado): void {
    const compromissosAtuais = this.compromissosSubject.value;
    const index = compromissosAtuais.findIndex(c => c.id === compromissoAtualizado.id);
    
    if (index !== -1) {
      const novosCompromissos = [...compromissosAtuais];
      novosCompromissos[index] = compromissoAtualizado;
      this.compromissosSubject.next(novosCompromissos);
      this.salvarCompromissosNoCache(novosCompromissos);
    }
  }

  removerCompromisso(id: string): void {
    const compromissosAtuais = this.compromissosSubject.value;
    const novosCompromissos = compromissosAtuais.filter(c => c.id !== id);
    this.compromissosSubject.next(novosCompromissos);
    this.salvarCompromissosNoCache(novosCompromissos);
  }

  limparCache(): void {
    localStorage.removeItem(this.CACHE_KEY);
    localStorage.removeItem(this.CACHE_TIMESTAMP_KEY);
    this.compromissosSubject.next([]);
  }

  private isCacheValido(): boolean {
    const timestamp = localStorage.getItem(this.CACHE_TIMESTAMP_KEY);
    if (!timestamp) return false;

    const cacheTime = parseInt(timestamp, 10);
    const agora = Date.now();
    
    return (agora - cacheTime) < this.CACHE_DURATION;
  }

  private obterCompromissosDoCache(): CompromissoCustomizado[] {
    try {
      const compromissosJson = localStorage.getItem(this.CACHE_KEY);
      if (!compromissosJson) return [];
      
      const compromissos = JSON.parse(compromissosJson);
      return Array.isArray(compromissos) ? compromissos : [];
    } catch (error) {
      console.error('Erro ao recuperar compromissos customizados do cache:', error);
      return [];
    }
  }

  private salvarCompromissosNoCache(compromissos: CompromissoCustomizado[]): void {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(compromissos));
      localStorage.setItem(this.CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Erro ao salvar compromissos customizados no cache:', error);
    }
  }
} 